import {
  API_CONSOLE_METHODS,
  adminOpenApiOperationSchema,
  type AdminOpenApi,
  type ApiConsoleMethod,
} from '@steado/admin-contract/overview';

/**
 * The OpenAPI document, flattened into something a request builder can drive.
 *
 * A pure function over the document rather than logic inside the component:
 * this is where every judgement about the spec lives — which methods are
 * offered, how parameters are split, what an operation is called when it has no
 * summary — and all of it is worth testing without a browser.
 */

export interface ApiOperationParameter {
  readonly name: string;
  readonly in: 'path' | 'query' | 'header' | 'cookie';
  readonly required: boolean;
  readonly description: string | undefined;
}

export interface ApiOperation {
  /** Stable across renders and safe in a URL — the open operation is a link. */
  readonly id: string;
  readonly method: ApiConsoleMethod;
  readonly path: string;
  readonly summary: string;
  readonly description: string | undefined;
  readonly tag: string;
  readonly requiresAuth: boolean;
  /**
   * Who may call this, read off the route's own guard list rather than guessed
   * from the method.
   *
   * A heuristic would be wrong in both directions here:
   * `POST /admin/meal-patterns/preview` computes without writing and is open to
   * `support`, while `GET /admin/users/:id/health` is a read only `admin` may
   * make. `admin-audited` is that one — every call to it writes an audit row,
   * which an operator should know before pressing Send.
   *
   * Undefined on a document generated before the extension existed, which the
   * console renders as an unknown rather than as "anyone".
   */
  readonly role: 'public' | 'support' | 'admin' | 'admin-audited' | undefined;
  readonly parameters: readonly ApiOperationParameter[];
  readonly hasRequestBody: boolean;
}

const ROLES = ['public', 'support', 'admin', 'admin-audited'] as const;

function toRole(value: unknown): ApiOperation['role'] {
  return (ROLES as readonly string[]).includes(value as string) ? (value as ApiOperation['role']) : undefined;
}

const PARAMETER_LOCATIONS = ['path', 'query', 'header', 'cookie'] as const;

/**
 * Every operation in the document, grouped-friendly and sorted.
 *
 * Sorted by tag then path so the list reads like the API's own table of
 * contents rather than by whatever order the generator emitted. Methods outside
 * `API_CONSOLE_METHODS` are dropped: `options` and `head` are not things anyone
 * opens a console to send.
 */
export function toOperations(document: AdminOpenApi): ApiOperation[] {
  const operations: ApiOperation[] = [];

  for (const [path, methods] of Object.entries(document.paths)) {
    for (const [method, raw] of Object.entries(methods)) {
      if (!isConsoleMethod(method)) continue;

      const parsed = adminOpenApiOperationSchema.safeParse(raw);
      if (!parsed.success) continue;

      const operation = parsed.data;

      operations.push({
        id: `${method}:${path}`,
        method,
        path,
        // A path is a worse label than a sentence and a better one than nothing.
        summary: operation.summary ?? path,
        description: operation.description,
        tag: operation.tags?.[0] ?? 'other',
        // Presence of a security requirement, not its contents: the console
        // sends as the signed-in admin and has one credential to offer.
        requiresAuth: (operation.security?.length ?? 0) > 0,
        // The schema is `.passthrough()`, so the extension survives parsing.
        role: toRole((operation as Record<string, unknown>)['x-steado-role']),
        parameters: (operation.parameters ?? []).flatMap((parameter) =>
          isParameterLocation(parameter.in)
            ? [
                {
                  name: parameter.name,
                  in: parameter.in,
                  required: parameter.required ?? parameter.in === 'path',
                  description: parameter.description,
                },
              ]
            : [],
        ),
        hasRequestBody: operation.requestBody !== undefined,
      });
    }
  }

  return operations.sort(
    (a, b) => a.tag.localeCompare(b.tag) || a.path.localeCompare(b.path) || a.method.localeCompare(b.method),
  );
}

/** Distinct tags in the order the sorted operation list presents them. */
export function toTags(operations: readonly ApiOperation[]): string[] {
  return [...new Set(operations.map((operation) => operation.tag))];
}

/**
 * Substitutes `{id}`-style path parameters.
 *
 * A placeholder left unfilled stays in the path rather than becoming an empty
 * segment: `/users//plan` would 404 with no clue why, while `/users/{id}/plan`
 * is visibly unfinished.
 */
export function buildPath(path: string, values: Readonly<Record<string, string>>): string {
  return path.replace(/\{(\w+)\}/g, (placeholder, name: string) => {
    const value = values[name];
    return value === undefined || value === '' ? placeholder : encodeURIComponent(value);
  });
}

/** Path parameters the operation declares but the form has not filled. */
export function missingPathParameters(
  operation: ApiOperation,
  values: Readonly<Record<string, string>>,
): string[] {
  return operation.parameters
    .filter((parameter) => parameter.in === 'path')
    .filter((parameter) => (values[parameter.name] ?? '') === '')
    .map((parameter) => parameter.name);
}

function isConsoleMethod(value: string): value is ApiConsoleMethod {
  return (API_CONSOLE_METHODS as readonly string[]).includes(value);
}

function isParameterLocation(value: string): value is ApiOperationParameter['in'] {
  return (PARAMETER_LOCATIONS as readonly string[]).includes(value);
}
